import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import DownloadForm from '../../components/common/DownloadForm'

const BridgingYouthMentalHealthAccessGap: React.FC = () => {
  const navigate = useNavigate()

  const contentRef = useRef<HTMLDivElement | null>(null)
  const [progress, setProgress] = useState(0)
  const [activeId, setActiveId] = useState<string>('exec-summary')
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  const sections = [
    { id: 'exec-summary' },
    { id: 'demand-surge' },
    { id: 'capacity-alone' },
    { id: 'access-breaks-down' },
    { id: 'schools-front-door' },
    { id: 'eligibility-bottleneck' },
    { id: 'coordination-lever' },
    { id: 'intelligent-automation' },
    { id: 'implementation-roadmap' },
    { id: 'measuring' },
    { id: 'conclusion' },
    { id: 'about' },
    { id: 'references' },
  ]

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const total = el.scrollHeight - window.innerHeight + rect.top
      const header = document.getElementById('navigation')
      const headerOffset = header ? header.offsetHeight + 16 : 80
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - headerOffset), 0),
        Math.max(total, 1)
      )
      setProgress((scrolled / Math.max(total, 1)) * 100)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const observers: IntersectionObserver[] = []
    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveId(id)
          })
        },
        { rootMargin: '-40% 0px -50% 0px', threshold: 0.01 }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const header = document.getElementById('navigation')
    const headerOffset = header ? header.offsetHeight + 16 : 80
    window.scrollTo({ top: el.offsetTop - headerOffset, behavior: 'smooth' })
  }

  const MetaItem = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
      <span className="font-medium text-slate-700">{label}:</span>
      <span>{value}</span>
    </div>
  )

  const imageBase = '/Image/WhitePapers/Bridging the Youth Mental Health Access Gap'
  const img1 = `${imageBase}/Bridging the Youth Mental Health Access Gap Image 1.png`
  const img2 = `${imageBase}/Bridging the Youth Mental Health Access Gap Image 2.png`
  const img3 = `${imageBase}/Bridging the Youth Mental Health Access Gap Image 3.png`

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <div className="fixed inset-x-0 top-0 z-50 h-1 bg-slate-200">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-indigo-500 transition-[width]"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="pt-20 pb-10 border-b border-slate-200 bg-[radial-gradient(1200px_600px_at_10%_-10%,#e8f1ff_0%,#f3f7ff_30%,#ffffff_65%)]">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            White Paper | Youth Mental Health
          </div>

          <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900">
            Bridging the Youth Mental Health Access Gap
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 text-base md:text-lg">
            Why Coordination and Eligibility Automation — Not Capacity Alone — Unlock Timely Care
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <MetaItem label="Author" value="EceptionistCX Research Group" />
            <MetaItem label="Published" value="January 2026" />
            <MetaItem label="Length" value="14 min read" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => setDownloadFormOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download PDF
            </button>
            <button
              onClick={() => navigator.share?.({ title: 'Bridging the Youth Mental Health Access Gap', url: window.location.href })}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-slate-800 shadow-sm hover:bg-slate-50"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
              Share
            </button>
          </div>
        </div>
      </header>

      <main ref={contentRef} className="mx-auto max-w-6xl px-4 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <article className="lg:col-span-8">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className={`scroll-mt-20 ${i !== 0 ? 'mt-10' : ''}`}>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">
                {s.id === 'exec-summary' && '⭐ Executive Summary'}
                {s.id === 'demand-surge' && '📈 1. The Youth Mental Health Demand Surge'}
                {s.id === 'capacity-alone' && '⚠️ 2. Why Capacity Alone Will Not Solve the Problem'}
                {s.id === 'access-breaks-down' && '🔀 3. Where Access Breaks Down for Children and Families'}
                {s.id === 'schools-front-door' && '🏫 4. Schools as the Mental Health Front Door'}
                {s.id === 'eligibility-bottleneck' && '📋 5. Eligibility Verification: The Hidden Access Bottleneck'}
                {s.id === 'coordination-lever' && '🤝 6. Coordination as the Primary Lever for Expanding Access'}
                {s.id === 'intelligent-automation' && '⚙️ 7. The Role of Intelligent Automation'}
                {s.id === 'implementation-roadmap' && '🛠️ 8. Implementation Roadmap'}
                {s.id === 'measuring' && '📊 9. Measuring What Matters'}
                {s.id === 'conclusion' && '✅ Conclusion'}
                {s.id === 'about' && 'ℹ️ About EceptionistCX'}
                {s.id === 'references' && '📚 References'}
              </h2>
              <div className="mt-4 prose prose-slate max-w-none">
                {s.id === 'exec-summary' && (
                  <>
                    <p>
                      Youth mental health needs have risen sharply, with schools increasingly serving as the first point of identification and referral for children and adolescents who require behavioral health support. While clinician shortages are real and well documented, access to care is constrained just as significantly by fragmented workflows, administrative burden, and breakdowns in coordination between schools, families, providers, and payers.
                    </p>
                    <br />
                    <p>
                      Many children who are identified as needing services never receive timely care—not because services do not exist, but because referrals are misrouted, eligibility is unclear, scheduling is delayed, or follow-up fails. Manual processes and disconnected systems contribute to long wait times, missed appointments, and significant drop-off between referral and treatment initiation.
                    </p>
                    <br />
                    <p>
                      This white paper argues that the youth mental health access crisis is as much a coordination and administrative problem as it is a capacity problem. By modernizing referral pathways, scheduling workflows, and eligibility verification through structured, digital, and automated processes, communities can unlock existing capacity, reduce delays, and improve outcomes for children and families—without placing additional strain on clinicians or school staff.
                    </p>
                  </>
                )}

                {s.id === 'demand-surge' && (
                  <>
                    <p>
                      Rates of anxiety, depression, and emotional distress among children and adolescents have increased substantially in recent years. National surveys indicate that nearly 40% of high school students report persistent feelings of sadness or hopelessness, and a significant percentage report poor mental health or suicidal ideation. These trends intensified during and after the COVID-19 pandemic and have not returned to pre-pandemic baselines.
                    </p>
                    <br />
                    <p>
                      At the same time, schools have become central to mental health identification and early intervention. Teachers, counselors, and school psychologists are often the first adults to recognize warning signs, particularly for children who may not otherwise access care. School-based mental health programs have expanded rapidly, but staffing levels and workflows have not kept pace with rising demand.
                    </p>
                    <br />
                    <p>
                      This mismatch has created a paradox: more awareness and identification, but limited throughput from identification to treatment. The result is frustration for families, burnout for school staff, and prolonged suffering for students.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">Figure 1. Youth Mental Health Indicators</h5>
                      <img src={img1} alt="Youth Mental Health Indicators (CDC YRBS 2023)" className="w-full rounded-lg shadow-sm" />
                      <p className="mt-2 text-sm text-slate-600">
                        Source: CDC Youth Risk Behavior Survey (YRBS), 2023. Percentages shown are among U.S. high school students.
                      </p>
                    </div>
                  </>
                )}

                {s.id === 'capacity-alone' && (
                  <>
                    <p>
                      Much of the public conversation around youth mental health focuses on clinician shortages. While shortages are real—particularly in rural and underserved areas—capacity constraints alone do not fully explain access failures.
                    </p>
                    <br />
                    <p>
                      Across behavioral health settings, a significant portion of provider capacity is lost to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Missed appointments and no-shows</li>
                      <li>Incomplete or inappropriate referrals</li>
                      <li>Delays in intake processing</li>
                      <li>Administrative rework caused by eligibility or authorization issues</li>
                    </ul>
                    <br />
                    <p>
                      Studies of behavioral health services routinely report initial appointment no-show rates exceeding 30%. Each missed appointment represents lost clinical capacity that cannot be easily recovered. Adding more providers without addressing these inefficiencies often results in more complexity rather than improved access.
                    </p>
                    <br />
                    <p>
                      In other words, access breaks down not only because there are too few clinicians, but because existing capacity is poorly coordinated and difficult to access.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">Figure 2. Lost Capacity from Missed Appointments</h5>
                      <img src={img2} alt="Lost Capacity from Missed Appointments (Behavioral Health)" className="w-full rounded-lg shadow-sm" />
                      <p className="mt-2 text-sm text-slate-600">
                        Illustrative example based on published behavioral health no-show research. Values reflect reported mean baseline no-show rates and example reductions with operational interventions.
                      </p>
                    </div>
                  </>
                )}

                {s.id === 'access-breaks-down' && (
                  <>
                    <p>
                      For a child identified as needing mental health services, the path from concern to care is rarely linear. Common breakdown points include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Referral initiation and intake collection (often manual and inconsistent)</li>
                      <li>Service mismatch (referrals sent to the wrong level of care or wrong program)</li>
                      <li>Consent and documentation delays (particularly when families are hard to reach)</li>
                      <li>Scheduling friction (multiple parties, limited self-service, repeated back-and-forth)</li>
                      <li>Eligibility uncertainty identified too late, causing rework or cancellation</li>
                      <li>Lack of feedback loops (schools and families lack visibility into next steps)</li>
                    </ul>
                    <br />
                    <p>
                      These breakdowns are intensified by demand pressures on schools and community providers. Where school-based services exist, they are often overwhelmed—leading to longer waitlists and increased handoffs to external networks.
                    </p>
                    <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
                      <h5 className="text-base font-semibold mb-2 text-slate-900">Figure 3. School-Based Mental Health: Utilization and Rising Demand</h5>
                      <img src={img3} alt="School-Based Mental Health: Utilization and Rising Demand" className="w-full rounded-lg shadow-sm" />
                      <p className="mt-2 text-sm text-slate-600">
                        Source: Kaiser Family Foundation (KFF) reporting on school-based mental health services and demand trends.
                      </p>
                    </div>
                  </>
                )}

                {s.id === 'schools-front-door' && (
                  <>
                    <p>
                      Schools increasingly function as the “front door” to mental health services. Teachers, counselors, and school psychologists are often the first adults to recognize warning signs—especially for students who may not have stable access to primary care or community-based services.
                    </p>
                    <br />
                    <p>
                      However, schools are not equipped to manage healthcare workflows involving service routing, eligibility checks, scheduling logistics, and longitudinal follow-up. To function effectively as a front door, schools need:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Structured, guided referral pathways (guardrails that help route to the right service)</li>
                      <li>Clear visibility into referral outcomes (scheduled, seen, declined, unable to contact)</li>
                      <li>Minimal administrative burden on school staff</li>
                      <li>Secure, role-based communication with families and providers</li>
                    </ul>
                    <br />
                    <p>
                      Without these supports, the burden falls on individual counselors and administrators, producing inconsistent outcomes and contributing to burnout.
                    </p>
                  </>
                )}

                {s.id === 'eligibility-bottleneck' && (
                  <>
                    <p>
                      Eligibility verification is one of the most significant—and often overlooked—barriers to timely access. Coverage rules vary by payer, plan, and service type. Manual eligibility checks introduce delays, rework, and uncertainty for families, especially when a child’s coverage status changes or when services must be delivered by a network-specific provider.
                    </p>
                    <br />
                    <p>
                      When eligibility issues are discovered late, the system pays for it through rework: resubmitting referrals, redirecting to a different provider network, rescheduling appointments, or losing the family altogether. In youth mental health, where engagement is fragile and families may already be overwhelmed, each additional step increases the risk of drop-off.
                    </p>
                    <br />
                    <p>
                      Embedding automated eligibility checks at key points—such as referral acceptance and scheduling—helps surface coverage constraints early, route referrals appropriately, and reduce avoidable churn. The goal is to remove administrative friction while keeping clinical decision-making in the hands of clinicians.
                    </p>
                  </>
                )}

                {s.id === 'coordination-lever' && (
                  <>
                    <p>
                      The youth mental health access challenge is best understood as a coordination problem spanning multiple systems: education, healthcare, community providers, and payers. Modern coordination requires more than information exchange—it requires workflow orchestration that guides users through complex processes, enforces completeness, and provides two-way visibility.
                    </p>
                    <br />
                    <p>
                      Key elements of an effective coordination model include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Guided referral workflows with built-in guardrails</li>
                      <li>Role-based portals for schools, families, and community providers</li>
                      <li>Two-way visibility into referral and scheduling status</li>
                      <li>Self-service scheduling supported by automated reminders</li>
                      <li>Automated eligibility checks embedded into referral and scheduling workflows</li>
                      <li>Longitudinal tracking across episodes of care</li>
                    </ul>
                  </>
                )}

                {s.id === 'intelligent-automation' && (
                  <>
                    <p>
                      Automation in mental health should be framed carefully. The goal is not to replace clinicians or automate clinical decision-making, but to reduce administrative friction so that human expertise can be applied where it matters most.
                    </p>
                    <br />
                    <p>
                      Appropriate uses of automation include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Routing referrals to the correct service based on structured criteria</li>
                      <li>Identifying incomplete referrals before submission</li>
                      <li>Flagging eligibility risks early in the workflow</li>
                      <li>Automating reminders and follow-ups</li>
                      <li>Using operational signals to identify likely no-show risk (for proactive outreach)</li>
                    </ul>
                  </>
                )}

                {s.id === 'implementation-roadmap' && (
                  <>
                    <p>
                      Organizations can adopt a phased approach to improving youth mental health access:
                    </p>
                    <br />
                    <p className="font-semibold">Phase 1: Standardize Referrals</p>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li>Define minimum referral datasets and completeness requirements</li>
                      <li>Implement guided referral pathways and guardrails</li>
                      <li>Align consent and documentation requirements across programs</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Phase 2: Improve Scheduling and Engagement</p>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li>Enable self-service scheduling for families and care teams</li>
                      <li>Automate reminders and confirmations</li>
                      <li>Provide visibility into appointment outcomes and next steps</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Phase 3: Embed Eligibility Automation</p>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li>Automate eligibility checks at key workflow points</li>
                      <li>Route referrals based on network and coverage constraints early</li>
                      <li>Reduce downstream cancellations and rework</li>
                    </ul>
                    <br />
                    <p className="font-semibold">Phase 4: Measure and Optimize</p>
                    <ul className="list-disc pl-6 space-y-1 mt-1">
                      <li>Track referral conversion and time-to-first-visit</li>
                      <li>Monitor no-show trends and capacity utilization</li>
                      <li>Use dashboards to identify bottlenecks and improve equity</li>
                    </ul>
                  </>
                )}

                {s.id === 'measuring' && (
                  <>
                    <p>
                      A modern coordination model should be measurable. Recommended metrics include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mt-2">
                      <li>Referral completeness rate</li>
                      <li>Wrong-service referral rate (misrouting)</li>
                      <li>Median time-to-first-appointment</li>
                      <li>No-show rate</li>
                      <li>Eligibility exception rate (coverage issues identified)</li>
                      <li>Referral-to-first-visit conversion rate</li>
                    </ul>
                    <br />
                    <p>
                      These measures enable continuous improvement, support accountability, and help demonstrate impact to funders, boards, and policymakers.
                    </p>
                  </>
                )}

                {s.id === 'conclusion' && (
                  <>
                    <p>
                      The youth mental health access crisis is real, urgent, and solvable. While expanding clinical capacity is important, meaningful improvements will not occur without addressing the coordination and administrative barriers that prevent children from receiving timely care.
                    </p>
                    <br />
                    <p>
                      By modernizing referral workflows, scheduling processes, and eligibility verification, communities can unlock existing capacity, reduce delays, and ensure that children receive the support they need—when they need it.
                    </p>
                    <br />
                    <p>
                      Technology should not replace human care. It should make human care possible.
                    </p>
                    <button
                      onClick={() => setDownloadFormOpen(true)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download Full Paper
                    </button>
                  </>
                )}

                {s.id === 'about' && (
                  <>
                    <p>
                      EceptionistCX is a collaborative care coordination platform designed to support complex referral, scheduling, and engagement workflows across healthcare, education, and community-based settings. The platform helps organizations reduce administrative burden, improve visibility across stakeholders, and create clearer pathways to care—particularly in environments where coordination across multiple parties is essential.
                    </p>
                  </>
                )}

                {s.id === 'references' && (
                  <ol className="list-decimal pl-6 space-y-2">
                    <li>Centers for Disease Control and Prevention (CDC). Youth Risk Behavior Survey (YRBS), 2023.</li>
                    <li>Kaiser Family Foundation (KFF). Reporting on school-based mental health services and demand trends.</li>
                    <li>Selected behavioral health appointment no-show research (systematic review / published interventions).</li>
                    <li>Health Resources and Services Administration (HRSA). Mental Health Professional Shortage Areas.</li>
                    <li>American Academy of Pediatrics (AAP). Mental health policy and prevalence reporting.</li>
                    <li>CAQH Index. Administrative transaction cost and savings opportunity reporting.</li>
                  </ol>
                )}
              </div>
            </section>
          ))}
        </article>

        <aside className="lg:col-span-4 lg:pl-2">
          <div className="lg:sticky lg:top-32 space-y-6">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
              <p className="mt-2 text-sm text-slate-700">
                This white paper argues that the youth mental health access crisis is as much a coordination and administrative problem as it is a capacity problem, and shows how modernizing referral pathways, scheduling, and eligibility verification can unlock existing capacity and improve outcomes.
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 list-disc pl-5">
                <li>Coordination and eligibility automation</li>
                <li>Schools as the mental health front door</li>
                <li>Phased implementation roadmap</li>
                <li>Measurable coordination metrics</li>
              </ul>
              <button
                onClick={() => setDownloadFormOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-white shadow hover:bg-slate-800"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                Download PDF
              </button>
            </div>

            <nav className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Table of Contents</div>
              <ul className="mt-3 space-y-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => scrollTo(s.id)}
                      className={
                        'text-left text-sm w-full rounded-lg px-2 py-1 hover:bg-slate-50 ' +
                        (activeId === s.id ? 'font-semibold text-slate-900' : 'text-slate-700')
                      }
                    >
                      {s.id === 'exec-summary' && '⭐ Executive Summary'}
                      {s.id === 'demand-surge' && '📈 1. The Youth Mental Health Demand Surge'}
                      {s.id === 'capacity-alone' && '⚠️ 2. Why Capacity Alone Will Not Solve the Problem'}
                      {s.id === 'access-breaks-down' && '🔀 3. Where Access Breaks Down'}
                      {s.id === 'schools-front-door' && '🏫 4. Schools as the Mental Health Front Door'}
                      {s.id === 'eligibility-bottleneck' && '📋 5. Eligibility Verification'}
                      {s.id === 'coordination-lever' && '🤝 6. Coordination as the Primary Lever'}
                      {s.id === 'intelligent-automation' && '⚙️ 7. The Role of Intelligent Automation'}
                      {s.id === 'implementation-roadmap' && '🛠️ 8. Implementation Roadmap'}
                      {s.id === 'measuring' && '📊 9. Measuring What Matters'}
                      {s.id === 'conclusion' && '✅ Conclusion'}
                      {s.id === 'about' && 'ℹ️ About EceptionistCX'}
                      {s.id === 'references' && '📚 References'}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            <div id="cite" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">Citation</div>
              <p className="mt-2 text-xs text-slate-700">
                EceptionistCX Research Group. &quot;Bridging the Youth Mental Health Access Gap: Why Coordination and Eligibility Automation — Not Capacity Alone — Unlock Timely Care.&quot; January 2026.
              </p>
              <button
                onClick={() => {
                  const text = 'EceptionistCX Research Group. "Bridging the Youth Mental Health Access Gap: Why Coordination and Eligibility Automation — Not Capacity Alone — Unlock Timely Care." January 2026.'
                  navigator.clipboard?.writeText(text)
                }}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
                Copy Citation
              </button>
            </div>
          </div>
        </aside>
      </main>

      <section className="border-t border-slate-200 bg-slate-50 py-10">
        <div className="mx-auto max-w-6xl px-4">
          <h3 className="text-xl md:text-2xl font-semibold text-slate-900">Related White Papers</h3>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Breaking Silos with Integrated Care Coordination', description: 'How integrated platforms improve outcomes and reduce costs across healthcare systems.', route: '/whitepapers/integrated-care' },
              { title: 'The Future of Referral Management in Healthcare', description: 'From fax to AI-powered workflows that eliminate administrative waste and improve access.', route: '/whitepapers/referral-cx' },
              { title: 'Digital Front Doors in Healthcare', description: 'Why scheduling matters more than ever for patient experience and provider efficiency.', route: '/whitepapers/scheduling-cx' },
            ].map((card, index: number) => (
              <button
                key={index}
                onClick={() => navigate(card.route)}
                className="group block rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <h4 className="text-base font-semibold text-slate-900 group-hover:underline">
                    {card.title}
                  </h4>
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-white">→</span>
                </div>
                <p className="mt-2 text-sm text-slate-700">{card.description}</p>
              </button>
            ))}
          </div>
          <div className="mt-6">
            <button
              onClick={() => navigate('/whitepapers')}
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              ← Back to White Papers
            </button>
          </div>
        </div>
      </section>

      <div className="h-8" />

      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Bridging the Youth Mental Health Access Gap: Why Coordination and Eligibility Automation — Not Capacity Alone — Unlock Timely Care"
        pdfPath="/PDF/WhitePapers/Youth_Mental_Health_Access_WhitePaper.pdf"
      />
    </div>
  )
}

export default BridgingYouthMentalHealthAccessGap
